import { Inject, Injectable } from "@angular/core";
import { STARTUP_CONTEXT_TOKEN, StartupContext } from "@obg/ngx.core";

import { DataBus } from "./data-bus";
import { EventBus } from "./event-bus";
import { T_DATA_BUS, T_EVENT_BUS } from "./injection";
import { MountingStrategy } from "./mounting/strategies/mounting.strategy";
import { StencilModuleMountingStrategy } from "./mounting/strategies/stencil-module-mounting.strategy";
import { RemoteModule } from "./remote-module.type";

@Injectable({
	providedIn: "root",
})
export class RemoteModuleFactory {
	private modules: Record<string, Promise<RemoteModule | undefined>> = {
		shell: Promise.resolve(undefined),
	};

	private mountingStrategies: Record<string, MountingStrategy> = {
		stencil: this.stencilModuleMountingStrategy,
	};

	constructor(
		private stencilModuleMountingStrategy: StencilModuleMountingStrategy,
		@Inject(T_DATA_BUS) private dataBus: DataBus,
		@Inject(T_EVENT_BUS) private eventBus: EventBus,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {}

	async create(identifier: string): Promise<void> {
		if (identifier in this.modules) {
			return;
		}
		if (!this.startupContext.remoteModuleConfigByIdentifier) {
			throw new Error(`[Boot Failure] Remote Module "${identifier}" does not have a configured URI.`);
		}

		const config = this.startupContext.remoteModuleConfigByIdentifier[identifier];
		const path = config.jsPath.replace(/(.*\/)[^\/]+$/, "$1index.esm.js");

		this.modules[identifier] = import(/*webpackIgnore:true*/ path); // eslint-disable-line spaced-comment

		const module = await this.modules[identifier];

		if (!module?.moduleFactory) {
			return;
		}

		const remoteModule = module.moduleFactory({
			dataBus: this.dataBus.createFacade(),
			eventBus: this.eventBus.createFacade(identifier, (identifierToLoad) => this.create(identifierToLoad)),
			configuration: undefined,
		});

		if (remoteModule && remoteModule?.mountingStrategy in this.mountingStrategies) {
			await this.mountingStrategies[remoteModule.mountingStrategy].mount(module);
		}
	}
}
